/**
 * Maturity Diagram Templates for Zero Trust Journey
 *
 * Defines architecture diagram templates for each maturity level per function.
 * Each template contains nodes, edges, annotations, and key capabilities.
 *
 * Node structure:
 * - id: unique identifier
 * - label: display name
 * - icon: lucide icon name
 * - x, y: position on canvas
 * - category: node category (user, identity, policy, application, etc.)
 * - isNew: true if new at this maturity level (vs previous)
 * - isHighlight: true to highlight as key component
 *
 * Edge structure:
 * - id: unique identifier
 * - source: source node id
 * - target: target node id
 * - label: optional edge label
 * - isNew: true if new at this maturity level
 * - style: 'solid' | 'dashed' | 'dotted'
 */

/**
 * Common node dimensions
 */
export const NODE_DIMENSIONS = {
  width: 140,
  height: 70,
  inputWidth: 100,
  inputHeight: 60
};

/**
 * Canvas dimensions for journey diagrams
 */
export const CANVAS_DIMENSIONS = {
  width: 800,
  height: 450
};

/**
 * ID_AUTH - Authentication Maturity Diagrams
 */
const ID_AUTH_TEMPLATES = {
  TRADITIONAL: {
    name: 'Password-Only Authentication',
    description: 'Static password-based authentication without multi-factor authentication',
    nodes: [
      { id: 'users', label: 'Users', icon: 'users', x: 40, y: 180, category: 'user', isInput: true },
      { id: 'password-auth', label: 'Password\nAuthentication', icon: 'key', x: 200, y: 180, category: 'identity' },
      { id: 'ldap', label: 'LDAP / AD', icon: 'database', x: 380, y: 180, category: 'identity' },
      { id: 'apps', label: 'Applications', icon: 'layers', x: 560, y: 180, category: 'application', isOutput: true }
    ],
    edges: [
      { id: 'e1', source: 'users', target: 'password-auth', label: 'Username/Password', style: 'solid' },
      { id: 'e2', source: 'password-auth', target: 'ldap', label: 'Validate', style: 'solid' },
      { id: 'e3', source: 'ldap', target: 'apps', label: 'Grant Access', style: 'solid' }
    ],
    annotations: [
      { id: 'a1', x: 200, y: 100, text: 'Single factor only - vulnerable to credential theft', type: 'warning' },
      { id: 'a2', x: 380, y: 270, text: 'Static credentials persist until manually revoked', type: 'info' }
    ],
    keyCapabilities: [
      'Password-based authentication',
      'No MFA requirement',
      'Static access credentials',
      'Manual credential revocation'
    ],
    risks: [
      'Credential theft via phishing',
      'Password spraying attacks',
      'No protection against compromised credentials',
      'Limited audit trail for authentication'
    ]
  },

  INITIAL: {
    name: 'Multi-Factor Authentication',
    description: 'MFA deployed with multiple factors including passwords and validation of entity attributes',
    nodes: [
      { id: 'users', label: 'Users', icon: 'users', x: 40, y: 200, category: 'user', isInput: true },
      { id: 'idp', label: 'Identity\nProvider', icon: 'shield', x: 180, y: 200, category: 'identity', isNew: true },
      { id: 'password', label: 'Password', icon: 'key', x: 320, y: 120, category: 'identity' },
      { id: 'mfa', label: 'MFA\n(SMS/TOTP)', icon: 'smartphone', x: 320, y: 280, category: 'identity', isNew: true, isHighlight: true },
      { id: 'attr-validation', label: 'Attribute\nValidation', icon: 'check-circle', x: 460, y: 200, category: 'policy', isNew: true },
      { id: 'apps', label: 'Applications', icon: 'layers', x: 620, y: 200, category: 'application', isOutput: true }
    ],
    edges: [
      { id: 'e1', source: 'users', target: 'idp', label: 'Authenticate', style: 'solid' },
      { id: 'e2', source: 'idp', target: 'password', label: '1st Factor', style: 'solid' },
      { id: 'e3', source: 'idp', target: 'mfa', label: '2nd Factor', style: 'solid', isNew: true },
      { id: 'e4', source: 'password', target: 'attr-validation', style: 'solid' },
      { id: 'e5', source: 'mfa', target: 'attr-validation', style: 'solid', isNew: true },
      { id: 'e6', source: 'attr-validation', target: 'apps', label: 'Validated', style: 'solid' }
    ],
    annotations: [
      { id: 'a1', x: 320, y: 350, text: 'Second factor reduces credential theft risk', type: 'success' },
      { id: 'a2', x: 460, y: 130, text: 'Validates user/device attributes before access', type: 'info' }
    ],
    keyCapabilities: [
      'MFA deployed with multiple factors',
      'Password plus SMS/TOTP codes',
      'Validation of entity attributes',
      'Centralized identity provider'
    ],
    risks: [
      'SMS-based MFA vulnerable to SIM swapping',
      'TOTP codes can be phished in real-time',
      'Still relies on knowledge-based factors',
      'Limited context-aware policies'
    ]
  },

  ADVANCED: {
    name: 'Phishing-Resistant MFA',
    description: 'Phishing-resistant MFA (FIDO2, PIV) with passwordless options and context-aware authentication',
    nodes: [
      { id: 'users', label: 'Users', icon: 'users', x: 30, y: 200, category: 'user', isInput: true },
      { id: 'idp', label: 'Identity\nProvider', icon: 'shield', x: 140, y: 200, category: 'identity' },
      { id: 'fido2', label: 'FIDO2 /\nPasskeys', icon: 'key', x: 270, y: 100, category: 'identity', isNew: true, isHighlight: true },
      { id: 'piv', label: 'PIV /\nSmartcard', icon: 'lock', x: 270, y: 200, category: 'identity', isNew: true },
      { id: 'passwordless', label: 'Passwordless', icon: 'user-check', x: 270, y: 300, category: 'identity', isNew: true, isHighlight: true },
      { id: 'context', label: 'Context\nEngine', icon: 'activity', x: 420, y: 200, category: 'policy', isNew: true },
      { id: 'policy', label: 'Policy\nDecision', icon: 'shield-check', x: 560, y: 200, category: 'policy', isNew: true },
      { id: 'apps', label: 'Applications', icon: 'layers', x: 700, y: 200, category: 'application', isOutput: true }
    ],
    edges: [
      { id: 'e1', source: 'users', target: 'idp', style: 'solid' },
      { id: 'e2', source: 'idp', target: 'fido2', label: 'Phishing-resistant', style: 'solid', isNew: true },
      { id: 'e3', source: 'idp', target: 'piv', style: 'solid', isNew: true },
      { id: 'e4', source: 'idp', target: 'passwordless', style: 'solid', isNew: true },
      { id: 'e5', source: 'fido2', target: 'context', style: 'solid' },
      { id: 'e6', source: 'piv', target: 'context', style: 'solid' },
      { id: 'e7', source: 'passwordless', target: 'context', style: 'solid' },
      { id: 'e8', source: 'context', target: 'policy', label: 'Risk Score', style: 'solid', isNew: true },
      { id: 'e9', source: 'policy', target: 'apps', label: 'Conditional', style: 'solid' }
    ],
    annotations: [
      { id: 'a1', x: 270, y: 40, text: 'FIDO2 keys cannot be phished - bound to origin', type: 'success' },
      { id: 'a2', x: 420, y: 280, text: 'Device, location, behavior signals inform risk', type: 'info' },
      { id: 'a3', x: 560, y: 130, text: 'Step-up authentication for high-risk actions', type: 'info' }
    ],
    keyCapabilities: [
      'Phishing-resistant MFA (FIDO2, PIV)',
      'Passwordless authentication options',
      'Context-aware authentication policies',
      'Risk-based conditional access'
    ],
    risks: [
      'Hardware key loss/theft requires recovery process',
      'Not all apps support modern authentication',
      'Context signals may have gaps',
      'Implementation complexity'
    ]
  },

  OPTIMAL: {
    name: 'Continuous Authentication',
    description: 'Continuous validation with real-time risk signals adjusting authentication throughout sessions',
    nodes: [
      { id: 'users', label: 'Users', icon: 'users', x: 20, y: 200, category: 'user', isInput: true },
      { id: 'idp', label: 'Identity\nProvider', icon: 'shield', x: 120, y: 200, category: 'identity' },
      { id: 'fido2', label: 'FIDO2 /\nPasskeys', icon: 'key', x: 230, y: 100, category: 'identity' },
      { id: 'continuous', label: 'Continuous\nAuth', icon: 'activity', x: 230, y: 300, category: 'identity', isNew: true, isHighlight: true },
      { id: 'risk-engine', label: 'Real-time\nRisk Engine', icon: 'shield-alert', x: 360, y: 200, category: 'policy', isNew: true, isHighlight: true },
      { id: 'ml-ueba', label: 'ML / UEBA', icon: 'git-branch', x: 360, y: 340, category: 'policy', isNew: true },
      { id: 'dynamic-policy', label: 'Dynamic\nPolicy', icon: 'shield-check', x: 500, y: 200, category: 'policy', isNew: true },
      { id: 'step-up', label: 'Step-up\nAuth', icon: 'lock', x: 500, y: 340, category: 'identity', isNew: true },
      { id: 'apps', label: 'Applications', icon: 'layers', x: 650, y: 200, category: 'application', isOutput: true }
    ],
    edges: [
      { id: 'e1', source: 'users', target: 'idp', style: 'solid' },
      { id: 'e2', source: 'idp', target: 'fido2', style: 'solid' },
      { id: 'e3', source: 'idp', target: 'continuous', label: 'Session signals', style: 'solid', isNew: true },
      { id: 'e4', source: 'fido2', target: 'risk-engine', style: 'solid' },
      { id: 'e5', source: 'continuous', target: 'risk-engine', label: 'Behavioral', style: 'dashed', isNew: true },
      { id: 'e6', source: 'ml-ueba', target: 'risk-engine', label: 'Anomaly', style: 'dashed', isNew: true },
      { id: 'e7', source: 'risk-engine', target: 'dynamic-policy', label: 'Live risk', style: 'solid', isNew: true },
      { id: 'e8', source: 'dynamic-policy', target: 'apps', label: 'Adaptive', style: 'solid' },
      { id: 'e9', source: 'dynamic-policy', target: 'step-up', label: 'High risk', style: 'dashed', isNew: true },
      { id: 'e10', source: 'step-up', target: 'risk-engine', label: 'Re-verify', style: 'dashed', isNew: true }
    ],
    annotations: [
      { id: 'a1', x: 230, y: 370, text: 'Session continuously monitored for anomalies', type: 'success' },
      { id: 'a2', x: 360, y: 130, text: 'ML models detect behavioral deviations in real-time', type: 'info' },
      { id: 'a3', x: 500, y: 130, text: 'Policies adapt dynamically to risk changes', type: 'success' },
      { id: 'a4', x: 500, y: 400, text: 'Step-up auth triggered on elevated risk', type: 'info' }
    ],
    keyCapabilities: [
      'Continuous validation throughout sessions',
      'Real-time risk signal integration',
      'ML/AI-driven behavioral analytics (UEBA)',
      'Dynamic step-up authentication on risk'
    ],
    risks: [
      'Complex to implement and maintain',
      'May impact user experience if too sensitive',
      'Requires mature data pipelines',
      'False positives can disrupt workflows'
    ]
  }
};

/**
 * ID_STORES - Identity Stores Maturity Diagrams
 */
const ID_STORES_TEMPLATES = {
  TRADITIONAL: {
    name: 'Disconnected Identity Stores',
    description: 'Multiple disconnected identity stores with manual provisioning and no single source of truth',
    nodes: [
      { id: 'users', label: 'Users', icon: 'users', x: 40, y: 200, category: 'user', isInput: true },
      { id: 'hr-system', label: 'HR System', icon: 'database', x: 200, y: 100, category: 'identity' },
      { id: 'ad', label: 'Active\nDirectory', icon: 'database', x: 200, y: 200, category: 'identity' },
      { id: 'ldap', label: 'LDAP', icon: 'database', x: 200, y: 300, category: 'identity' },
      { id: 'manual', label: 'Manual\nProvisioning', icon: 'edit2', x: 380, y: 200, category: 'process' },
      { id: 'apps', label: 'Applications', icon: 'layers', x: 560, y: 200, category: 'application', isOutput: true }
    ],
    edges: [
      { id: 'e1', source: 'users', target: 'hr-system', style: 'dashed' },
      { id: 'e2', source: 'users', target: 'ad', style: 'solid' },
      { id: 'e3', source: 'users', target: 'ldap', style: 'dashed' },
      { id: 'e4', source: 'hr-system', target: 'manual', style: 'dashed' },
      { id: 'e5', source: 'ad', target: 'manual', style: 'dashed' },
      { id: 'e6', source: 'ldap', target: 'manual', style: 'dashed' },
      { id: 'e7', source: 'manual', target: 'apps', label: 'Inconsistent', style: 'dashed' }
    ],
    annotations: [
      { id: 'a1', x: 200, y: 40, text: 'Multiple disconnected identity stores', type: 'warning' },
      { id: 'a2', x: 380, y: 280, text: 'Manual processes lead to errors and delays', type: 'warning' }
    ],
    keyCapabilities: [
      'Multiple identity repositories',
      'Manual user provisioning',
      'Manual deprovisioning',
      'No single source of truth'
    ],
    risks: [
      'Orphaned accounts remain active',
      'Inconsistent access across systems',
      'Slow onboarding/offboarding',
      'Compliance gaps and audit failures'
    ]
  },

  INITIAL: {
    name: 'Consolidated Identity Stores',
    description: 'Identity stores consolidated where possible with some automated provisioning and basic lifecycle management',
    nodes: [
      { id: 'users', label: 'Users', icon: 'users', x: 40, y: 200, category: 'user', isInput: true },
      { id: 'hr-system', label: 'HR System', icon: 'database', x: 180, y: 100, category: 'identity' },
      { id: 'idp', label: 'Central\nIdP', icon: 'shield', x: 180, y: 200, category: 'identity', isNew: true, isHighlight: true },
      { id: 'legacy', label: 'Legacy\nSystems', icon: 'database', x: 180, y: 300, category: 'identity' },
      { id: 'auto-prov', label: 'Automated\nProvisioning', icon: 'settings', x: 340, y: 150, category: 'process', isNew: true },
      { id: 'lifecycle', label: 'Lifecycle\nManagement', icon: 'activity', x: 340, y: 260, category: 'process', isNew: true },
      { id: 'apps', label: 'Applications', icon: 'layers', x: 520, y: 200, category: 'application', isOutput: true }
    ],
    edges: [
      { id: 'e1', source: 'users', target: 'idp', label: 'Primary', style: 'solid' },
      { id: 'e2', source: 'hr-system', target: 'idp', label: 'Sync', style: 'solid', isNew: true },
      { id: 'e3', source: 'legacy', target: 'idp', label: 'Federate', style: 'dashed' },
      { id: 'e4', source: 'idp', target: 'auto-prov', style: 'solid', isNew: true },
      { id: 'e5', source: 'idp', target: 'lifecycle', style: 'solid', isNew: true },
      { id: 'e6', source: 'auto-prov', target: 'apps', style: 'solid' },
      { id: 'e7', source: 'lifecycle', target: 'apps', label: 'JML events', style: 'solid' }
    ],
    annotations: [
      { id: 'a1', x: 180, y: 130, text: 'Central IdP consolidates most identities', type: 'success' },
      { id: 'a2', x: 340, y: 90, text: 'HR changes trigger automated provisioning', type: 'info' }
    ],
    keyCapabilities: [
      'Consolidated identity stores',
      'Some automated provisioning',
      'Basic lifecycle management',
      'Central identity provider'
    ],
    risks: [
      'Legacy systems may remain disconnected',
      'Partial automation gaps',
      'Federation complexity',
      'Inconsistent attribute mappings'
    ]
  },

  ADVANCED: {
    name: 'Authoritative Identity Source',
    description: 'Single authoritative identity source with automated lifecycle management and identity federation',
    nodes: [
      { id: 'users', label: 'Users', icon: 'users', x: 30, y: 200, category: 'user', isInput: true },
      { id: 'hr-auth', label: 'HR\n(Authoritative)', icon: 'database', x: 140, y: 100, category: 'identity', isHighlight: true },
      { id: 'idp', label: 'Central\nIdP', icon: 'shield', x: 140, y: 200, category: 'identity' },
      { id: 'scim', label: 'SCIM\nProvisioning', icon: 'settings', x: 280, y: 100, category: 'process', isNew: true, isHighlight: true },
      { id: 'auto-lifecycle', label: 'Automated\nLifecycle', icon: 'activity', x: 280, y: 200, category: 'process', isNew: true },
      { id: 'federation', label: 'Identity\nFederation', icon: 'git-branch', x: 280, y: 300, category: 'identity', isNew: true },
      { id: 'governance', label: 'Identity\nGovernance', icon: 'shield-check', x: 440, y: 200, category: 'policy', isNew: true },
      { id: 'apps', label: 'Applications', icon: 'layers', x: 600, y: 200, category: 'application', isOutput: true }
    ],
    edges: [
      { id: 'e1', source: 'users', target: 'idp', style: 'solid' },
      { id: 'e2', source: 'hr-auth', target: 'idp', label: 'Source of truth', style: 'solid', isNew: true },
      { id: 'e3', source: 'idp', target: 'scim', label: 'Push', style: 'solid', isNew: true },
      { id: 'e4', source: 'idp', target: 'auto-lifecycle', style: 'solid' },
      { id: 'e5', source: 'idp', target: 'federation', label: 'SAML/OIDC', style: 'solid', isNew: true },
      { id: 'e6', source: 'scim', target: 'governance', style: 'solid' },
      { id: 'e7', source: 'auto-lifecycle', target: 'governance', style: 'solid' },
      { id: 'e8', source: 'federation', target: 'governance', style: 'solid' },
      { id: 'e9', source: 'governance', target: 'apps', label: 'Governed', style: 'solid' }
    ],
    annotations: [
      { id: 'a1', x: 140, y: 40, text: 'HR system is the single source of truth', type: 'success' },
      { id: 'a2', x: 280, y: 40, text: 'SCIM automates cross-system provisioning', type: 'info' },
      { id: 'a3', x: 440, y: 130, text: 'Governance enforces access policies', type: 'info' }
    ],
    keyCapabilities: [
      'Authoritative identity source',
      'Automated lifecycle management',
      'Identity federation (SAML/OIDC)',
      'Identity governance platform'
    ],
    risks: [
      'Single point of failure for identity',
      'Complex federation trust relationships',
      'Governance rule maintenance',
      'Migration complexity from legacy'
    ]
  },

  OPTIMAL: {
    name: 'Unified Identity Fabric',
    description: 'Unified identity fabric with real-time synchronization, full automation, and self-service capabilities',
    nodes: [
      { id: 'users', label: 'Users', icon: 'users', x: 20, y: 200, category: 'user', isInput: true },
      { id: 'self-service', label: 'Self-Service\nPortal', icon: 'user-check', x: 120, y: 100, category: 'identity', isNew: true },
      { id: 'identity-fabric', label: 'Identity\nFabric', icon: 'grid', x: 120, y: 200, category: 'identity', isNew: true, isHighlight: true },
      { id: 'did', label: 'Decentralized\nIdentity', icon: 'globe', x: 120, y: 320, category: 'identity', isNew: true },
      { id: 'real-time-sync', label: 'Real-time\nSync', icon: 'activity', x: 280, y: 120, category: 'process', isNew: true, isHighlight: true },
      { id: 'full-automation', label: 'Full\nAutomation', icon: 'settings', x: 280, y: 220, category: 'process', isNew: true },
      { id: 'ai-governance', label: 'AI-Driven\nGovernance', icon: 'git-branch', x: 440, y: 170, category: 'policy', isNew: true, isHighlight: true },
      { id: 'apps', label: 'All\nApplications', icon: 'layers', x: 600, y: 200, category: 'application', isOutput: true },
      { id: 'cloud-apps', label: 'Cloud\nApps', icon: 'globe', x: 600, y: 100, category: 'application', isOutput: true },
      { id: 'on-prem', label: 'On-Prem', icon: 'server', x: 600, y: 300, category: 'application', isOutput: true }
    ],
    edges: [
      { id: 'e1', source: 'users', target: 'self-service', style: 'solid', isNew: true },
      { id: 'e2', source: 'users', target: 'identity-fabric', style: 'solid' },
      { id: 'e3', source: 'users', target: 'did', style: 'dashed', isNew: true },
      { id: 'e4', source: 'self-service', target: 'real-time-sync', style: 'solid' },
      { id: 'e5', source: 'identity-fabric', target: 'real-time-sync', style: 'solid', isNew: true },
      { id: 'e6', source: 'identity-fabric', target: 'full-automation', style: 'solid' },
      { id: 'e7', source: 'did', target: 'full-automation', style: 'dashed' },
      { id: 'e8', source: 'real-time-sync', target: 'ai-governance', style: 'solid', isNew: true },
      { id: 'e9', source: 'full-automation', target: 'ai-governance', style: 'solid' },
      { id: 'e10', source: 'ai-governance', target: 'apps', style: 'solid' },
      { id: 'e11', source: 'ai-governance', target: 'cloud-apps', style: 'solid' },
      { id: 'e12', source: 'ai-governance', target: 'on-prem', style: 'solid' }
    ],
    annotations: [
      { id: 'a1', x: 120, y: 130, text: 'Unified fabric spans all identity systems', type: 'success' },
      { id: 'a2', x: 280, y: 60, text: 'Changes propagate instantly across all systems', type: 'success' },
      { id: 'a3', x: 440, y: 100, text: 'AI optimizes access and detects anomalies', type: 'info' },
      { id: 'a4', x: 120, y: 380, text: 'Optional decentralized identity support', type: 'info' }
    ],
    keyCapabilities: [
      'Unified identity fabric',
      'Real-time synchronization',
      'Full automation of identity lifecycle',
      'Self-service with appropriate controls',
      'AI-driven governance'
    ],
    risks: [
      'High implementation complexity',
      'Requires mature DevOps practices',
      'AI model maintenance and bias',
      'Decentralized identity adoption challenges'
    ]
  }
};

/**
 * ID_RISK - Risk Assessments Maturity Diagrams
 */
const ID_RISK_TEMPLATES = {
  TRADITIONAL: {
    name: 'No Identity Risk Assessment',
    description: 'Access decisions based solely on credentials without identity-based risk assessment',
    nodes: [
      { id: 'users', label: 'Users', icon: 'users', x: 40, y: 200, category: 'user', isInput: true },
      { id: 'credentials', label: 'Credentials', icon: 'key', x: 200, y: 200, category: 'identity' },
      { id: 'static-rules', label: 'Static\nRules', icon: 'file-text', x: 380, y: 200, category: 'policy' },
      { id: 'apps', label: 'Applications', icon: 'layers', x: 560, y: 200, category: 'application', isOutput: true }
    ],
    edges: [
      { id: 'e1', source: 'users', target: 'credentials', label: 'Authenticate', style: 'solid' },
      { id: 'e2', source: 'credentials', target: 'static-rules', label: 'Check', style: 'solid' },
      { id: 'e3', source: 'static-rules', target: 'apps', label: 'Allow/Deny', style: 'solid' }
    ],
    annotations: [
      { id: 'a1', x: 380, y: 130, text: 'No risk context considered', type: 'warning' },
      { id: 'a2', x: 200, y: 280, text: 'Valid credentials = full access', type: 'warning' }
    ],
    keyCapabilities: [
      'Credential-based authentication',
      'Static access rules',
      'No risk scoring',
      'Binary access decisions'
    ],
    risks: [
      'Stolen credentials grant full access',
      'No detection of compromised accounts',
      'Cannot adapt to threat intelligence',
      'No behavioral analysis'
    ]
  },

  INITIAL: {
    name: 'Basic Identity Risk Scoring',
    description: 'Basic risk scoring for high-privilege accounts with manual risk assessments during access reviews',
    nodes: [
      { id: 'users', label: 'Users', icon: 'users', x: 40, y: 200, category: 'user', isInput: true },
      { id: 'idp', label: 'Identity\nProvider', icon: 'shield', x: 160, y: 200, category: 'identity' },
      { id: 'risk-score', label: 'Risk\nScoring', icon: 'activity', x: 300, y: 120, category: 'policy', isNew: true, isHighlight: true },
      { id: 'manual-review', label: 'Manual\nReview', icon: 'user-check', x: 300, y: 280, category: 'process', isNew: true },
      { id: 'access-rules', label: 'Access\nRules', icon: 'shield-check', x: 450, y: 200, category: 'policy' },
      { id: 'apps', label: 'Applications', icon: 'layers', x: 600, y: 200, category: 'application', isOutput: true }
    ],
    edges: [
      { id: 'e1', source: 'users', target: 'idp', style: 'solid' },
      { id: 'e2', source: 'idp', target: 'risk-score', label: 'High-priv', style: 'solid', isNew: true },
      { id: 'e3', source: 'idp', target: 'manual-review', label: 'Periodic', style: 'dashed', isNew: true },
      { id: 'e4', source: 'risk-score', target: 'access-rules', style: 'solid' },
      { id: 'e5', source: 'manual-review', target: 'access-rules', style: 'dashed' },
      { id: 'e6', source: 'access-rules', target: 'apps', style: 'solid' }
    ],
    annotations: [
      { id: 'a1', x: 300, y: 60, text: 'Risk scoring for privileged accounts', type: 'success' },
      { id: 'a2', x: 300, y: 350, text: 'Manual reviews catch some anomalies', type: 'info' }
    ],
    keyCapabilities: [
      'Basic risk scoring for high-privilege accounts',
      'Manual risk assessments',
      'Periodic access reviews',
      'Risk-informed access rules'
    ],
    risks: [
      'Standard users not risk-scored',
      'Manual reviews are slow',
      'Point-in-time assessments',
      'Limited threat intelligence'
    ]
  },

  ADVANCED: {
    name: 'Continuous Identity Risk Scoring',
    description: 'Continuous identity risk scoring integrated with access decisions and behavioral analytics',
    nodes: [
      { id: 'users', label: 'Users', icon: 'users', x: 30, y: 200, category: 'user', isInput: true },
      { id: 'idp', label: 'Identity\nProvider', icon: 'shield', x: 130, y: 200, category: 'identity' },
      { id: 'behavior', label: 'Behavioral\nAnalytics', icon: 'activity', x: 260, y: 100, category: 'policy', isNew: true, isHighlight: true },
      { id: 'continuous-risk', label: 'Continuous\nRisk Score', icon: 'shield-alert', x: 260, y: 200, category: 'policy', isNew: true },
      { id: 'threat-intel', label: 'Threat\nIntelligence', icon: 'globe', x: 260, y: 300, category: 'policy', isNew: true },
      { id: 'risk-engine', label: 'Risk\nEngine', icon: 'git-branch', x: 410, y: 200, category: 'policy', isNew: true, isHighlight: true },
      { id: 'adaptive-policy', label: 'Adaptive\nPolicy', icon: 'shield-check', x: 550, y: 200, category: 'policy', isNew: true },
      { id: 'apps', label: 'Applications', icon: 'layers', x: 690, y: 200, category: 'application', isOutput: true }
    ],
    edges: [
      { id: 'e1', source: 'users', target: 'idp', style: 'solid' },
      { id: 'e2', source: 'idp', target: 'behavior', style: 'solid', isNew: true },
      { id: 'e3', source: 'idp', target: 'continuous-risk', style: 'solid', isNew: true },
      { id: 'e4', source: 'threat-intel', target: 'continuous-risk', label: 'Enrich', style: 'dashed', isNew: true },
      { id: 'e5', source: 'behavior', target: 'risk-engine', style: 'solid' },
      { id: 'e6', source: 'continuous-risk', target: 'risk-engine', style: 'solid', isNew: true },
      { id: 'e7', source: 'risk-engine', target: 'adaptive-policy', label: 'Score', style: 'solid', isNew: true },
      { id: 'e8', source: 'adaptive-policy', target: 'apps', label: 'Conditional', style: 'solid' }
    ],
    annotations: [
      { id: 'a1', x: 260, y: 40, text: 'UEBA detects behavioral anomalies', type: 'success' },
      { id: 'a2', x: 410, y: 130, text: 'Risk engine combines multiple signals', type: 'info' },
      { id: 'a3', x: 260, y: 370, text: 'External threat intel enriches risk data', type: 'info' }
    ],
    keyCapabilities: [
      'Continuous identity risk scoring',
      'Behavioral analytics integration',
      'Threat intelligence enrichment',
      'Risk-based access decisions'
    ],
    risks: [
      'Alert fatigue from false positives',
      'Complex integration requirements',
      'Behavioral baseline drift',
      'Privacy considerations'
    ]
  },

  OPTIMAL: {
    name: 'Real-Time AI-Driven Risk Assessment',
    description: 'Real-time ML/AI-driven identity risk assessment with dynamic permission adjustments',
    nodes: [
      { id: 'users', label: 'Users', icon: 'users', x: 20, y: 200, category: 'user', isInput: true },
      { id: 'idp', label: 'Identity\nProvider', icon: 'shield', x: 110, y: 200, category: 'identity' },
      { id: 'ml-engine', label: 'ML / AI\nEngine', icon: 'git-branch', x: 230, y: 120, category: 'policy', isNew: true, isHighlight: true },
      { id: 'real-time-risk', label: 'Real-time\nRisk', icon: 'shield-alert', x: 230, y: 220, category: 'policy', isNew: true },
      { id: 'predictive', label: 'Predictive\nAnalytics', icon: 'activity', x: 230, y: 320, category: 'policy', isNew: true },
      { id: 'dynamic-perms', label: 'Dynamic\nPermissions', icon: 'settings', x: 380, y: 170, category: 'policy', isNew: true, isHighlight: true },
      { id: 'auto-response', label: 'Auto\nResponse', icon: 'shield-check', x: 380, y: 280, category: 'policy', isNew: true },
      { id: 'all-resources', label: 'All\nResources', icon: 'layers', x: 530, y: 120, category: 'application', isOutput: true },
      { id: 'apps', label: 'Applications', icon: 'layers', x: 530, y: 220, category: 'application', isOutput: true },
      { id: 'data', label: 'Data', icon: 'database', x: 530, y: 320, category: 'application', isOutput: true }
    ],
    edges: [
      { id: 'e1', source: 'users', target: 'idp', style: 'solid' },
      { id: 'e2', source: 'idp', target: 'ml-engine', label: 'Signals', style: 'solid', isNew: true },
      { id: 'e3', source: 'idp', target: 'real-time-risk', style: 'solid' },
      { id: 'e4', source: 'predictive', target: 'real-time-risk', label: 'Predict', style: 'dashed', isNew: true },
      { id: 'e5', source: 'ml-engine', target: 'dynamic-perms', style: 'solid', isNew: true },
      { id: 'e6', source: 'real-time-risk', target: 'dynamic-perms', style: 'solid' },
      { id: 'e7', source: 'real-time-risk', target: 'auto-response', label: 'High risk', style: 'dashed', isNew: true },
      { id: 'e8', source: 'dynamic-perms', target: 'all-resources', style: 'solid' },
      { id: 'e9', source: 'dynamic-perms', target: 'apps', style: 'solid' },
      { id: 'e10', source: 'dynamic-perms', target: 'data', style: 'solid' }
    ],
    annotations: [
      { id: 'a1', x: 230, y: 60, text: 'ML models continuously learn normal behavior', type: 'success' },
      { id: 'a2', x: 380, y: 100, text: 'Permissions adjust in real-time to risk', type: 'success' },
      { id: 'a3', x: 230, y: 380, text: 'Predictive analytics anticipate threats', type: 'info' },
      { id: 'a4', x: 380, y: 350, text: 'Automated response to high-risk events', type: 'info' }
    ],
    keyCapabilities: [
      'Real-time ML/AI identity risk assessment',
      'Predictive threat analytics',
      'Dynamic permission adjustments',
      'Automated risk response'
    ],
    risks: [
      'Model training data quality',
      'Explainability challenges',
      'Computational overhead',
      'Continuous model retraining needs'
    ]
  }
};

/**
 * ID_ACCESS - Access Management Maturity Diagrams
 */
const ID_ACCESS_TEMPLATES = {
  TRADITIONAL: {
    name: 'Static Role Assignments',
    description: 'Static role assignments with broad access grants and infrequent access reviews',
    nodes: [
      { id: 'users', label: 'Users', icon: 'users', x: 40, y: 200, category: 'user', isInput: true },
      { id: 'static-roles', label: 'Static\nRoles', icon: 'tag', x: 200, y: 200, category: 'policy' },
      { id: 'broad-access', label: 'Broad\nAccess', icon: 'globe', x: 380, y: 200, category: 'policy' },
      { id: 'apps', label: 'Applications', icon: 'layers', x: 560, y: 200, category: 'application', isOutput: true }
    ],
    edges: [
      { id: 'e1', source: 'users', target: 'static-roles', label: 'Assigned', style: 'solid' },
      { id: 'e2', source: 'static-roles', target: 'broad-access', label: 'Grants', style: 'solid' },
      { id: 'e3', source: 'broad-access', target: 'apps', label: 'Full access', style: 'solid' }
    ],
    annotations: [
      { id: 'a1', x: 200, y: 130, text: 'Roles assigned at hire, rarely updated', type: 'warning' },
      { id: 'a2', x: 380, y: 280, text: 'Excessive permissions accumulate over time', type: 'warning' }
    ],
    keyCapabilities: [
      'Static role assignments',
      'Broad access grants',
      'Infrequent access reviews',
      'Manual role management'
    ],
    risks: [
      'Privilege accumulation',
      'Over-provisioned access',
      'Stale permissions',
      'Insider threat risk'
    ]
  },

  INITIAL: {
    name: 'RBAC with Periodic Reviews',
    description: 'Role-based access control with periodic certification and some just-in-time access for privileged accounts',
    nodes: [
      { id: 'users', label: 'Users', icon: 'users', x: 40, y: 200, category: 'user', isInput: true },
      { id: 'rbac', label: 'RBAC\nEngine', icon: 'settings', x: 170, y: 200, category: 'policy', isNew: true, isHighlight: true },
      { id: 'jit-access', label: 'JIT Access\n(Privileged)', icon: 'lock', x: 310, y: 100, category: 'policy', isNew: true },
      { id: 'cert-review', label: 'Access\nCertification', icon: 'check-circle', x: 310, y: 300, category: 'process', isNew: true },
      { id: 'access-grants', label: 'Access\nGrants', icon: 'shield-check', x: 460, y: 200, category: 'policy' },
      { id: 'apps', label: 'Applications', icon: 'layers', x: 610, y: 200, category: 'application', isOutput: true }
    ],
    edges: [
      { id: 'e1', source: 'users', target: 'rbac', style: 'solid' },
      { id: 'e2', source: 'rbac', target: 'jit-access', label: 'Privileged', style: 'solid', isNew: true },
      { id: 'e3', source: 'rbac', target: 'access-grants', label: 'Standard', style: 'solid' },
      { id: 'e4', source: 'cert-review', target: 'rbac', label: 'Quarterly', style: 'dashed', isNew: true },
      { id: 'e5', source: 'jit-access', target: 'access-grants', style: 'solid' },
      { id: 'e6', source: 'access-grants', target: 'apps', style: 'solid' }
    ],
    annotations: [
      { id: 'a1', x: 170, y: 130, text: 'Roles define access permissions', type: 'success' },
      { id: 'a2', x: 310, y: 40, text: 'Just-in-time for admin access', type: 'info' },
      { id: 'a3', x: 310, y: 370, text: 'Periodic reviews catch orphaned access', type: 'info' }
    ],
    keyCapabilities: [
      'Role-based access control',
      'Periodic access certification',
      'JIT access for privileged accounts',
      'Access review workflows'
    ],
    risks: [
      'Role explosion complexity',
      'Reviews can be rubber-stamped',
      'JIT limited to privileged accounts',
      'Static role boundaries'
    ]
  },

  ADVANCED: {
    name: 'ABAC with JIT Access',
    description: 'Attribute-based access control with just-in-time access for most privileged operations',
    nodes: [
      { id: 'users', label: 'Users', icon: 'users', x: 30, y: 200, category: 'user', isInput: true },
      { id: 'abac', label: 'ABAC\nEngine', icon: 'git-branch', x: 140, y: 200, category: 'policy', isNew: true, isHighlight: true },
      { id: 'user-attrs', label: 'User\nAttributes', icon: 'user-check', x: 280, y: 80, category: 'identity' },
      { id: 'env-attrs', label: 'Environment\nAttributes', icon: 'globe', x: 280, y: 200, category: 'identity', isNew: true },
      { id: 'resource-attrs', label: 'Resource\nAttributes', icon: 'database', x: 280, y: 320, category: 'identity', isNew: true },
      { id: 'jit-engine', label: 'JIT Access\nEngine', icon: 'lock', x: 430, y: 120, category: 'policy', isNew: true, isHighlight: true },
      { id: 'auto-review', label: 'Automated\nReviews', icon: 'activity', x: 430, y: 280, category: 'process', isNew: true },
      { id: 'policy-decision', label: 'Policy\nDecision', icon: 'shield-check', x: 580, y: 200, category: 'policy', isNew: true },
      { id: 'apps', label: 'Applications', icon: 'layers', x: 720, y: 200, category: 'application', isOutput: true }
    ],
    edges: [
      { id: 'e1', source: 'users', target: 'abac', style: 'solid' },
      { id: 'e2', source: 'abac', target: 'user-attrs', style: 'solid' },
      { id: 'e3', source: 'abac', target: 'env-attrs', style: 'solid', isNew: true },
      { id: 'e4', source: 'abac', target: 'resource-attrs', style: 'solid', isNew: true },
      { id: 'e5', source: 'user-attrs', target: 'jit-engine', style: 'solid' },
      { id: 'e6', source: 'env-attrs', target: 'policy-decision', style: 'solid' },
      { id: 'e7', source: 'resource-attrs', target: 'policy-decision', style: 'solid' },
      { id: 'e8', source: 'jit-engine', target: 'policy-decision', label: 'Elevated', style: 'solid', isNew: true },
      { id: 'e9', source: 'auto-review', target: 'abac', label: 'Continuous', style: 'dashed', isNew: true },
      { id: 'e10', source: 'policy-decision', target: 'apps', style: 'solid' }
    ],
    annotations: [
      { id: 'a1', x: 140, y: 130, text: 'Attributes combine for rich policies', type: 'success' },
      { id: 'a2', x: 430, y: 60, text: 'JIT extends to most privileged ops', type: 'success' },
      { id: 'a3', x: 280, y: 260, text: 'Environment context (time, location, device)', type: 'info' }
    ],
    keyCapabilities: [
      'Attribute-based access control',
      'Multiple attribute sources',
      'JIT access for most privileged operations',
      'Regular automated reviews'
    ],
    risks: [
      'Policy complexity',
      'Attribute data quality',
      'JIT approval bottlenecks',
      'Audit trail complexity'
    ]
  },

  OPTIMAL: {
    name: 'Zero Standing Privileges',
    description: 'Dynamic access policies with zero standing privileges and automated real-time context-based decisions',
    nodes: [
      { id: 'users', label: 'Users', icon: 'users', x: 20, y: 200, category: 'user', isInput: true },
      { id: 'zsp', label: 'Zero Standing\nPrivileges', icon: 'shield', x: 120, y: 200, category: 'policy', isNew: true, isHighlight: true },
      { id: 'real-time-ctx', label: 'Real-time\nContext', icon: 'activity', x: 250, y: 100, category: 'identity', isNew: true },
      { id: 'ai-policy', label: 'AI-Driven\nPolicy', icon: 'git-branch', x: 250, y: 200, category: 'policy', isNew: true, isHighlight: true },
      { id: 'auto-decision', label: 'Automated\nDecisions', icon: 'settings', x: 250, y: 300, category: 'policy', isNew: true },
      { id: 'dynamic-grant', label: 'Dynamic\nGrant', icon: 'lock', x: 400, y: 150, category: 'policy', isNew: true },
      { id: 'time-bound', label: 'Time-bound\nAccess', icon: 'activity', x: 400, y: 260, category: 'policy', isNew: true },
      { id: 'continuous-eval', label: 'Continuous\nEvaluation', icon: 'shield-check', x: 540, y: 200, category: 'policy', isNew: true },
      { id: 'apps', label: 'Applications', icon: 'layers', x: 680, y: 200, category: 'application', isOutput: true }
    ],
    edges: [
      { id: 'e1', source: 'users', target: 'zsp', style: 'solid' },
      { id: 'e2', source: 'zsp', target: 'real-time-ctx', style: 'solid', isNew: true },
      { id: 'e3', source: 'zsp', target: 'ai-policy', style: 'solid', isNew: true },
      { id: 'e4', source: 'auto-decision', target: 'ai-policy', label: 'Instant', style: 'solid', isNew: true },
      { id: 'e5', source: 'real-time-ctx', target: 'dynamic-grant', style: 'solid' },
      { id: 'e6', source: 'ai-policy', target: 'dynamic-grant', style: 'solid' },
      { id: 'e7', source: 'ai-policy', target: 'time-bound', style: 'solid' },
      { id: 'e8', source: 'dynamic-grant', target: 'continuous-eval', style: 'solid', isNew: true },
      { id: 'e9', source: 'time-bound', target: 'continuous-eval', label: 'Expires', style: 'dashed', isNew: true },
      { id: 'e10', source: 'continuous-eval', target: 'apps', label: 'Adaptive', style: 'solid' },
      { id: 'e11', source: 'continuous-eval', target: 'zsp', label: 'Revoke', style: 'dashed', isNew: true }
    ],
    annotations: [
      { id: 'a1', x: 120, y: 130, text: 'No persistent privileged access', type: 'success' },
      { id: 'a2', x: 250, y: 40, text: 'Context evaluated in real-time', type: 'success' },
      { id: 'a3', x: 400, y: 90, text: 'Access granted just for needed duration', type: 'info' },
      { id: 'a4', x: 540, y: 280, text: 'Access continuously re-evaluated', type: 'info' }
    ],
    keyCapabilities: [
      'Zero standing privileges',
      'Dynamic policy evaluation',
      'Automated access decisions',
      'Real-time context-based access',
      'Continuous access evaluation'
    ],
    risks: [
      'Latency in access decisions',
      'Policy maintenance complexity',
      'User experience friction',
      'Dependency on real-time systems'
    ]
  }
};

/**
 * Export all templates organized by function code
 */
export const MATURITY_DIAGRAM_TEMPLATES = {
  ID_AUTH: ID_AUTH_TEMPLATES,
  ID_STORES: ID_STORES_TEMPLATES,
  ID_RISK: ID_RISK_TEMPLATES,
  ID_ACCESS: ID_ACCESS_TEMPLATES
};

/**
 * Get diagram template for a specific function and level
 */
export function getDiagramTemplate(functionCode, maturityLevel) {
  return MATURITY_DIAGRAM_TEMPLATES[functionCode]?.[maturityLevel] || null;
}

/**
 * Get all levels for a function
 */
export function getFunctionLevels(functionCode) {
  const templates = MATURITY_DIAGRAM_TEMPLATES[functionCode];
  if (!templates) return [];
  return ['TRADITIONAL', 'INITIAL', 'ADVANCED', 'OPTIMAL'].filter(
    level => templates[level]
  );
}

/**
 * Calculate what elements are new between two levels
 */
export function getLevelDiff(functionCode, fromLevel, toLevel) {
  const fromTemplate = getDiagramTemplate(functionCode, fromLevel);
  const toTemplate = getDiagramTemplate(functionCode, toLevel);

  if (!fromTemplate || !toTemplate) return null;

  const fromNodeIds = new Set(fromTemplate.nodes.map(n => n.id));
  const fromEdgeIds = new Set(fromTemplate.edges.map(e => e.id));

  return {
    newNodes: toTemplate.nodes.filter(n => !fromNodeIds.has(n.id) || n.isNew),
    newEdges: toTemplate.edges.filter(e => !fromEdgeIds.has(e.id) || e.isNew),
    removedNodes: fromTemplate.nodes.filter(n => !toTemplate.nodes.find(tn => tn.id === n.id)),
    removedEdges: fromTemplate.edges.filter(e => !toTemplate.edges.find(te => te.id === e.id))
  };
}

export default MATURITY_DIAGRAM_TEMPLATES;
